// @ts-nocheck
import { createRouter, createWebHistory, useRoute, useRouter } from 'vue-router'
import { computed, defineComponent, h, onMounted, ref, watch } from 'vue'
import HomeView from '@/views/HomeView.vue'
import LoginView from '@/views/LoginView.vue'
import RegisterView from '@/views/RegisterView.vue'

const KPView = defineComponent({
  name: 'KPView',
  setup() {
    const route = useRoute()
    const router = useRouter()

    const casePath = ref<string>(String(route.query.kp || route.query.case || '')) // keep legacy ?case=
    const apiBase = String(import.meta.env.VITE_API_BASE_URL || '').replace(/\/+$/, '')
    const kpBase = computed(() => {
      // Important: iframe requests cannot send Authorization headers, only cookies/query.
      // Cookies are domain-scoped (not port-scoped), so we must keep the same hostname as the frontend,
      // otherwise the backend will see "Missing token" and the viewer looks empty.
      const fallback = window.location.origin.replace(/:\d+$/, ':8000')
      if (!apiBase) return fallback
      try {
        const envHost = new URL(apiBase).hostname
        const winHost = window.location.hostname
        if (envHost && winHost && envHost !== winHost) return fallback
      } catch (_) {
        // ignore URL parse errors, just use apiBase
      }
      return apiBase
    })
    const mode = computed<'catalog' | 'report'>(() => (casePath.value ? 'report' : 'catalog'))
    const iframeNonce = ref(Date.now())
    const iframeReady = ref(false)
    const authHint = computed(() => {
      const kpTok = String(window.localStorage.getItem('kp_token') || '').trim()
      if (kpTok) return 'auth: kp_token'
      const jwtTok = String(window.localStorage.getItem('token') || '').trim()
      if (jwtTok) return 'auth: jwt'
      return 'auth: none'
    })
    
    const iframeSrc = computed(() => {
      if (!iframeReady.value) return ''
      const base = kpBase.value.replace(/\/+$/, '')
      if (!base) return ''
      const ts = iframeNonce.value
      if (mode.value === 'catalog') return `${base}/kp/catalog?_ts=${ts}`
      return `${base}/kp/report?case=${encodeURIComponent(casePath.value)}&_ts=${ts}`
    })

    const debugUrl = computed(() => {
      const base = kpBase.value.replace(/\/+$/, '')
      if (mode.value === 'catalog') return `${base}/kp/catalog`
      return `${base}/kp/report?case=${encodeURIComponent(casePath.value)}`
    })

    const setCookieFromLocalStorage = () => {
      const t = window.localStorage.getItem('token') || ''
      if (!t) return
      // Bridge auth to backend port for iframe pages.
      // NOTE: Cookie lifetime doesn't extend JWT exp.
      document.cookie = `token=${encodeURIComponent(t)}; Path=/; SameSite=Lax; Max-Age=31536000`
      const kp = window.localStorage.getItem('kp_token') || ''
      if (kp) document.cookie = `kp_token=${encodeURIComponent(kp)}; Path=/; SameSite=Lax; Max-Age=31536000`
    }

    const _joinRel = (a: string, b: string) => {
      const aa = String(a || '').replace(/^\/+|\/+$/g, '')
      const bb = String(b || '').replace(/^\/+|\/+$/g, '')
      if (!aa) return bb
      if (!bb) return aa
      return `${aa}/${bb}`
    }

    const _kpAuthHeaders = () => {
      const h: Record<string, string> = {}
      const kp = (window.localStorage.getItem('kp_token') || '').trim()
      if (kp) {
        h['X-KP-Token'] = kp
        return h
      }
      const jwt = (window.localStorage.getItem('token') || '').trim()
      if (jwt) h['Authorization'] = `Bearer ${jwt}`
      return h
    }

    const _fetchList = async (rel: string) => {
      setCookieFromLocalStorage()
      const base = kpBase.value.replace(/\/+$/, '')
      const url = `${base}/api/v1/kp/list?rel=${encodeURIComponent(rel || '')}&_ts=${Date.now()}`
      const resp = await fetch(url, { headers: _kpAuthHeaders(), cache: 'no-store' as any })
      if (!resp.ok) {
        const txt = await resp.text().catch(() => '')
        throw new Error(`${resp.status} ${resp.statusText}${txt ? `: ${txt}` : ''}`)
      }
      const data = await resp.json()
      return Array.isArray(data?.items) ? data.items : []
    }

    const _pMapLimit = async <T, R>(items: T[], limit: number, fn: (x: T) => Promise<R>) => {
      const out: R[] = new Array(items.length)
      let i = 0
      const workers = Array.from({ length: Math.max(1, Math.min(limit || 1, items.length || 1)) }, async () => {
        while (true) {
          const idx = i++
          if (idx >= items.length) break
          out[idx] = await fn(items[idx])
        }
      })
      await Promise.all(workers)
      return out
    }

    const _hasArtifacts = async (rel: string) => {
      const a = String(rel || '').replace(/\/+$/g, '')
      const tries = [`${a}/artifacts`, `${a} /artifacts`]
      for (const t of tries) {
        try {
          const items = await _fetchList(t)
          if (Array.isArray(items)) return true
        } catch (_) {
          // ignore
        }
      }
      return false
    }

    type KpPick = { label: string; rel: string }
    const pickerOpen = ref<boolean>(false)
    const pickerErr = ref<string>('')
    const pickerLoading = ref<boolean>(false)

    const projectFilter = ref<string>('')
    const projectItems = ref<Array<{ name: string; is_dir: boolean }>>([])
    const projectSelected = ref<string>('') // name

    const kpFilter = ref<string>('')
    const kpItems = ref<KpPick[]>([])
    const createOpen = ref<boolean>(false)
    const createName = ref<string>('')
    const createEdition = ref<string>('v1')
    const createErr = ref<string>('')
    const createBusy = ref<boolean>(false)

    const selectedProjectLabel = computed(() => projectSelected.value || '')
    const selectedKpLabel = computed(() => {
      if (!casePath.value) return ''
      const base = projectSelected.value ? `${projectSelected.value}/` : ''
      return casePath.value.startsWith(base) ? casePath.value.slice(base.length) : casePath.value
    })

    const projectFromKp = computed(() => {
      const parts = String(casePath.value || '').split('/').filter(Boolean)
      return parts[0] || ''
    })

    const loadProjects = async () => {
      pickerLoading.value = true
      pickerErr.value = ''
      try {
        const items = await _fetchList('')
        projectItems.value = (items || []).filter((x: any) => x && x.is_dir)
      } catch (e: any) {
        pickerErr.value = String(e?.message || e || 'load error')
        projectItems.value = []
      } finally {
        pickerLoading.value = false
      }
    }

    const loadKpsForProject = async (projectName: string) => {
      pickerLoading.value = true
      pickerErr.value = ''
      kpItems.value = []
      try {
        const relProject = String(projectName || '').replace(/^\/+|\/+$/g, '')
        if (!relProject) return
        const items = await _fetchList(relProject)
        const dirs = (items || []).filter((x: any) => x && x.is_dir).map((x: any) => String(x.name || ''))

        // For each top-level folder in the project:
        // - if it has <folder>/artifacts => selectable
        // - else if it has <folder>/editions/<edition>/artifacts => selectable by edition
        const picks: KpPick[] = []
        await _pMapLimit(
          dirs,
          8,
          async (dirName) => {
            const relTop = _joinRel(relProject, dirName)
            if (await _hasArtifacts(relTop)) {
              picks.push({ label: dirName, rel: relTop })
              return
            }
            // editions
            try {
              const edItems = await _fetchList(_joinRel(relTop, 'editions'))
              const edDirs = (edItems || []).filter((x: any) => x && x.is_dir).map((x: any) => String(x.name || ''))
              await _pMapLimit(
                edDirs,
                6,
                async (ed) => {
                  const relEd = _joinRel(_joinRel(relTop, 'editions'), ed)
                  if (await _hasArtifacts(relEd)) picks.push({ label: `${dirName} / ${ed}`, rel: relEd })
                }
              )
            } catch (_) {
              // no editions dir
            }
          }
        )

        // Stable sort by label
        kpItems.value = picks.sort((a, b) => a.label.localeCompare(b.label, 'ru'))
      } catch (e: any) {
        pickerErr.value = String(e?.message || e || 'load error')
        kpItems.value = []
      } finally {
        pickerLoading.value = false
      }
    }

    const createKp = async () => {
      createErr.value = ''
      const proj = String(projectSelected.value || '').trim()
      const name = String(createName.value || '').trim()
      const ed = String(createEdition.value || 'v1').trim() || 'v1'
      if (!proj) {
        createErr.value = 'Сначала выбери проект.'
        return
      }
      if (!name) {
        createErr.value = 'Название КП пустое.'
        return
      }
      createBusy.value = true
      try {
        setCookieFromLocalStorage()
        const base = kpBase.value.replace(/\/+$/, '')
        const url =
          `${base}/api/v1/kp/create-kp?project=${encodeURIComponent(proj)}` +
          `&kp_name=${encodeURIComponent(name)}` +
          `&edition=${encodeURIComponent(ed)}`
        const resp = await fetch(url, { method: 'POST', headers: _kpAuthHeaders() })
        const txt = await resp.text().catch(() => '')
        if (!resp.ok) throw new Error(`${resp.status} ${resp.statusText}${txt ? `: ${txt}` : ''}`)
        const data = txt ? JSON.parse(txt) : {}
        const rel = String(data?.rel || '')
        createOpen.value = false
        createName.value = ''
        createEdition.value = 'v1'
        await loadKpsForProject(proj)
        if (rel) pickKp(rel)
      } catch (e: any) {
        createErr.value = String(e?.message || e || 'create error')
      } finally {
        createBusy.value = false
      }
    }

    onMounted(() => {
      setCookieFromLocalStorage()
      setTimeout(() => {
        iframeReady.value = true
      }, 500)
    })

    watch(
      () => route.query.kp ?? route.query.case,
      (v) => {
        casePath.value = String(v || '')
        setCookieFromLocalStorage()
      }
    )

    const goCatalog = () => router.push({ path: '/kp', query: {} })
    const goReport = () => {
      if (!casePath.value) return
      router.push({ path: '/kp', query: { kp: casePath.value } })
    }
    const openNewTab = () => {
      if (!iframeSrc.value) return
      window.open(iframeSrc.value, '_blank', 'noopener,noreferrer')
    }

    // Google Drive transcripts sync (tenant-level)
    const gdriveSyncing = ref<boolean>(false)
    const gdriveSyncMsg = ref<string>('')

    const syncTranscriptsFromGDrive = async () => {
      if (gdriveSyncing.value) return
      gdriveSyncing.value = true
      gdriveSyncMsg.value = ''
      try {
        setCookieFromLocalStorage()
        const base = kpBase.value.replace(/\/+$/, '')
        const proj = String(projectFromKp.value || '').trim()
        const url = `${base}/api/v1/kp/transcripts/sync-gdrive${proj ? `?project=${encodeURIComponent(proj)}` : ''}`
        const resp = await fetch(url, { method: 'POST', headers: _kpAuthHeaders() })
        const txt = await resp.text().catch(() => '')
        if (!resp.ok) throw new Error(`${resp.status} ${resp.statusText}${txt ? `: ${txt}` : ''}`)
        const data = txt ? JSON.parse(txt) : {}
        const d = data?.stats?.downloaded
        gdriveSyncMsg.value = `✅ ${typeof d === 'number' ? `+${d}` : 'OK'}`
        if (trOpen.value) {
          await loadTrList()
        }
      } catch (e: any) {
        gdriveSyncMsg.value = `⚠️ ${String(e?.message || e || 'sync error')}`
      } finally {
        gdriveSyncing.value = false
      }
    }

    // Transcripts modal (links only)
    const trOpen = ref<boolean>(false)
    const trErr = ref<string>('')
    const trLoading = ref<boolean>(false)
    const trRel = ref<string>('') // current folder rel under Status-Projects (scoped to project)
    const trItems = ref<Array<{ name: string; is_dir: boolean; size?: number | null; mtime?: number | null }>>([])
    const trFilter = ref<string>('')
    const trSelected = ref<Record<string, boolean>>({}) // rel -> true

    const _trJoin = (a: string, b: string) => _joinRel(a, b)

    const loadTrList = async () => {
      trLoading.value = true
      trErr.value = ''
      try {
        const items = await _fetchList(trRel.value)
        trItems.value = items || []
      } catch (e: any) {
        trErr.value = String(e?.message || e || 'load error')
        trItems.value = []
      } finally {
        trLoading.value = false
      }
    }

    const loadTrManifest = async () => {
      trErr.value = ''
      try {
        const ed = String(casePath.value || '').trim()
        if (!ed) return
        setCookieFromLocalStorage()
        const base = kpBase.value.replace(/\/+$/, '')
        const url = `${base}/api/v1/kp/transcripts/get?edition_rel=${encodeURIComponent(ed)}`
        const resp = await fetch(url, { headers: _kpAuthHeaders() })
        const txt = await resp.text().catch(() => '')
        if (!resp.ok) throw new Error(`${resp.status} ${resp.statusText}${txt ? `: ${txt}` : ''}`)
        const data = txt ? JSON.parse(txt) : {}
        const files = data?.manifest?.files
        const next: Record<string, boolean> = {}
        if (Array.isArray(files)) {
          files.forEach((f: any) => {
            const rel = typeof f === 'string' ? f : String(f?.rel || '')
            if (rel) next[rel] = true
          })
        }
        trSelected.value = next
      } catch (e: any) {
        trErr.value = String(e?.message || e || 'manifest error')
      }
    }

    const saveTrManifest = async () => {
      trLoading.value = true
      trErr.value = ''
      try {
        const ed = String(casePath.value || '').trim()
        if (!ed) throw new Error('KP не выбрано')
        const files = Object.keys(trSelected.value || {}).filter((k) => trSelected.value[k])
        setCookieFromLocalStorage()
        const base = kpBase.value.replace(/\/+$/, '')
        const url = `${base}/api/v1/kp/transcripts/set?edition_rel=${encodeURIComponent(ed)}`
        const resp = await fetch(url, {
          method: 'POST',
          headers: { ..._kpAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ files })
        })
        const txt = await resp.text().catch(() => '')
        if (!resp.ok) throw new Error(`${resp.status} ${resp.statusText}${txt ? `: ${txt}` : ''}`)
        trOpen.value = false
      } catch (e: any) {
        trErr.value = String(e?.message || e || 'save error')
      } finally {
        trLoading.value = false
      }
    }

    const openTranscripts = async () => {
      if (!casePath.value) return
      trOpen.value = true
      trFilter.value = ''
      trErr.value = ''
      const proj = projectFromKp.value
      trRel.value = proj
      await loadTrManifest()
      await loadTrList()
    }

    const trUp = async () => {
      const proj = projectFromKp.value
      const parts = String(trRel.value || '').split('/').filter(Boolean)
      if (!parts.length) return
      if (parts[0] !== proj) {
        trRel.value = proj
      } else if (parts.length === 1) {
        trRel.value = proj
      } else {
        parts.pop()
        trRel.value = parts.join('/')
      }
      await loadTrList()
    }

    const trEnter = async (name: string) => {
      trRel.value = _trJoin(trRel.value, name)
      await loadTrList()
    }

    const trToggle = (fileRel: string) => {
      const cur = !!trSelected.value[fileRel]
      trSelected.value = { ...trSelected.value, [fileRel]: !cur }
    }

    const trClear = () => {
      trSelected.value = {}
    }

    const trFilteredDirs = computed(() => {
      const q = (trFilter.value || '').trim().toLowerCase()
      const dirs = (trItems.value || []).filter((x) => x && x.is_dir)
      if (!q) return dirs
      return dirs.filter((x) => String(x.name || '').toLowerCase().includes(q))
    })

    const trFilteredFiles = computed(() => {
      const q = (trFilter.value || '').trim().toLowerCase()
      const files = (trItems.value || []).filter((x) => x && !x.is_dir)
      if (!q) return files
      return files.filter((x) => String(x.name || '').toLowerCase().includes(q))
    })

    const trSelectedList = computed(() => Object.keys(trSelected.value || {}).filter((k) => trSelected.value[k]).sort())

    const openPicker = async () => {
      pickerOpen.value = true
      pickerErr.value = ''
      projectSelected.value = projectSelected.value || ''
      kpFilter.value = ''
      projectFilter.value = ''
      createOpen.value = false
      createErr.value = ''
      await loadProjects()
      if (projectSelected.value) await loadKpsForProject(projectSelected.value)
    }

    const pickProject = async (name: string) => {
      projectSelected.value = name
      kpFilter.value = ''
      createOpen.value = false
      createErr.value = ''
      await loadKpsForProject(name)
    }

    const pickKp = (rel: string) => {
      const next = String(rel || '').replace(/^\/+|\/+$/g, '')
      casePath.value = next
      iframeNonce.value = Date.now()
      pickerOpen.value = false
      router.push({ path: '/kp', query: { kp: next } })
    }

    const filteredProjects = computed(() => {
      const q = (projectFilter.value || '').trim().toLowerCase()
      const dirs = (projectItems.value || []).filter((x: any) => x && x.is_dir)
      if (!q) return dirs
      return dirs.filter((x: any) => String(x.name || '').toLowerCase().includes(q))
    })

    const filteredKps = computed(() => {
      const q = (kpFilter.value || '').trim().toLowerCase()
      const items = kpItems.value || []
      if (!q) return items
      return items.filter((x) => String(x.label || '').toLowerCase().includes(q))
    })

    return () =>
      h('div', { style: 'display:flex; flex-direction:column; height:100%; min-height:calc(100vh - 64px);' }, [
        h(
          'div',
          {
            style:
              'display:flex; gap:8px; align-items:center; padding:12px 16px; border-bottom:1px solid rgba(0,0,0,0.12);'
          },
          [
            h('strong', { style: 'margin-right:8px;' }, 'KP'),
            h('button', { onClick: goCatalog, style: 'padding:6px 10px; cursor:pointer;' }, 'Каталог'),
            h(
              'button',
              { onClick: openPicker, style: 'padding:6px 10px; cursor:pointer;' },
              selectedProjectLabel.value ? `Проект: ${selectedProjectLabel.value}` : 'Выбрать проект'
            ),
            h(
              'span',
              { style: 'flex:1; min-width:260px; opacity:0.75; font-size:12px; overflow:hidden; text-overflow:ellipsis;' },
              casePath.value ? `КП: ${selectedKpLabel.value}` : 'КП не выбрано'
            ),
            h('span', { style: 'opacity:0.65; font-size:12px;' }, authHint.value),
            h(
              'button',
              { onClick: openTranscripts, disabled: !casePath.value, style: 'padding:6px 10px; cursor:pointer;' },
              'Расшифровки...'
            ),
            h(
              'button',
              { onClick: syncTranscriptsFromGDrive, style: 'padding:6px 10px; cursor:pointer;', disabled: gdriveSyncing.value },
              gdriveSyncing.value ? '⏳ Обновить расшифровки с google drive' : 'Обновить расшифровки с google drive'
            ),
            gdriveSyncMsg.value ? h('span', { style: 'opacity:0.75; font-size:12px;' }, gdriveSyncMsg.value) : null,
            h(
              'button',
              { onClick: goReport, style: 'padding:6px 10px; cursor:pointer;', disabled: !casePath.value },
              'Отчёт'
            ),
            h('button', { onClick: openNewTab, style: 'padding:6px 10px; cursor:pointer;' }, 'Новая вкладка'),
            h('a', { href: debugUrl.value, target: '_blank', style: 'padding:6px 10px; cursor:pointer; font-size:12px; color:#666;' }, 'Direct Link')
          ]
        ),
        iframeReady.value ? h('iframe', {
          src: iframeSrc.value,
          style: 'flex:1; width:100%; border:0; background:#fff; min-height:0;'
        }) : h('div', { style: 'flex:1; display:flex; align-items:center; justify-content:center;' }, 'Загрузка...'),
        pickerOpen.value
          ? h(
              'div',
              {
                style:
                  'position:fixed; inset:0; background:rgba(0,0,0,0.35); display:flex; align-items:center; justify-content:center; z-index:9999;'
              },
              [
                h(
                  'div',
                  {
                    style:
                      'width:min(1100px, calc(100vw - 32px)); height:min(720px, calc(100vh - 32px)); background:#fff; border-radius:12px; overflow:hidden; display:flex; flex-direction:column; box-shadow:0 16px 60px rgba(0,0,0,0.35);'
                  },
                  [
                    h(
                      'div',
                      {
                        style:
                          'display:flex; align-items:center; justify-content:space-between; padding:12px 16px; border-bottom:1px solid rgba(0,0,0,0.12);'
                      },
                      [
                        h('strong', {}, 'Выбор проекта и КП'),
                        h('button', { onClick: () => (pickerOpen.value = false), style: 'padding:6px 10px; cursor:pointer;' }, 'Закрыть')
                      ]
                    ),
                    h(
                      'div',
                      { style: 'display:flex; flex:1; min-height:0;' },
                      [
                        // Projects column
                        h(
                          'div',
                          {
                            style:
                              'width:40%; border-right:1px solid rgba(0,0,0,0.12); padding:12px; overflow:auto; background:#fafafa;'
                          },
                          [
                            h('div', { style: 'display:flex; gap:8px; align-items:center; margin-bottom:8px;' }, [
                              h('strong', {}, 'Проекты'),
                              h(
                                'button',
                                { onClick: loadProjects, style: 'padding:6px 10px; cursor:pointer;' },
                                pickerLoading.value ? '...' : 'Обновить'
                              )
                            ]),
                            h('input', {
                              value: projectFilter.value,
                              placeholder: 'поиск проекта...',
                              onInput: (e: any) => (projectFilter.value = e?.target?.value || ''),
                              style: 'width:100%; padding:8px 10px; margin-bottom:10px;'
                            }),
                            h(
                              'div',
                              { style: 'display:flex; flex-direction:column; gap:6px;' },
                              filteredProjects.value.map((it: any) => {
                                const name = String(it?.name || '')
                                const sel = projectSelected.value === name
                                return h(
                                  'button',
                                  {
                                    key: name,
                                    onClick: () => pickProject(name),
                                    style:
                                      'text-align:left; padding:8px 10px; border:1px solid rgba(0,0,0,0.12); border-radius:10px; background:' +
                                      (sel ? '#e8f0fe' : '#fff') +
                                      '; cursor:pointer;'
                                  },
                                  name
                                )
                              })
                            )
                          ]
                        ),
                        // KP column
                        h(
                          'div',
                          { style: 'flex:1; padding:12px; overflow:auto;' },
                          [
                            h('div', { style: 'display:flex; gap:8px; align-items:center; margin-bottom:8px;' }, [
                              h('strong', {}, projectSelected.value ? `КП внутри: ${projectSelected.value}` : 'КП'),
                              projectSelected.value
                                ? h(
                                    'button',
                                    { onClick: () => loadKpsForProject(projectSelected.value), style: 'padding:6px 10px; cursor:pointer;' },
                                    pickerLoading.value ? '...' : 'Обновить'
                                  )
                                : null
                            ]),
                            projectSelected.value
                              ? h('div', { style: 'display:flex; gap:8px; align-items:center; margin-bottom:10px;' }, [
                                  h(
                                    'button',
                                    {
                                      onClick: () => {
                                        createOpen.value = !createOpen.value
                                        createErr.value = ''
                                      },
                                      style: 'padding:6px 10px; cursor:pointer;'
                                    },
                                    createOpen.value ? 'Отмена' : 'Создать новое КП'
                                  )
                                ])
                              : null,
                            createOpen.value
                              ? h('div', { style: 'border:1px solid rgba(0,0,0,0.12); border-radius:10px; padding:10px; margin-bottom:10px; background:#fafafa;' }, [
                                  h('div', { style: 'display:flex; gap:8px; align-items:center; margin-bottom:8px;' }, [
                                    h('input', {
                                      value: createName.value,
                                      placeholder: 'Название КП (папка)',
                                      onInput: (e: any) => (createName.value = e?.target?.value || ''),
                                      style: 'flex:1; padding:8px 10px;'
                                    }),
                                    h('input', {
                                      value: createEdition.value,
                                      placeholder: 'edition (v1)',
                                      onInput: (e: any) => (createEdition.value = e?.target?.value || ''),
                                      style: 'width:160px; padding:8px 10px;'
                                    }),
                                    h(
                                      'button',
                                      { onClick: createKp, disabled: createBusy.value, style: 'padding:8px 12px; cursor:pointer;' },
                                      createBusy.value ? '...' : 'Создать'
                                    )
                                  ]),
                                  createErr.value
                                    ? h('div', { style: 'color:#b00020; white-space:pre-wrap; font-size:12px;' }, createErr.value)
                                    : null
                                ])
                              : null,
                            h('input', {
                              value: kpFilter.value,
                              placeholder: 'поиск КП / edition...',
                              onInput: (e: any) => (kpFilter.value = e?.target?.value || ''),
                              style: 'width:100%; padding:8px 10px; margin-bottom:10px;'
                            }),
                            pickerErr.value
                              ? h('div', { style: 'color:#b00020; white-space:pre-wrap; font-size:12px; margin-bottom:10px;' }, pickerErr.value)
                              : null,
                            !projectSelected.value
                              ? h('div', { style: 'opacity:0.7; font-size:12px;' }, 'Сначала выбери проект слева.')
                              : null,
                            projectSelected.value && pickerLoading.value
                              ? h('div', { style: 'opacity:0.7; font-size:12px;' }, 'Сканирую папки и editions на наличие artifacts...')
                              : null,
                            h(
                              'div',
                              { style: 'display:flex; flex-direction:column; gap:6px;' },
                              filteredKps.value.map((kp) =>
                                h(
                                  'button',
                                  {
                                    key: kp.rel,
                                    onClick: () => pickKp(kp.rel),
                                    style:
                                      'text-align:left; padding:10px 12px; border:1px solid rgba(0,0,0,0.12); border-radius:10px; background:#fff; cursor:pointer;'
                                  },
                                  kp.label
                                )
                              )
                            ),
                            projectSelected.value && !pickerLoading.value && filteredKps.value.length === 0
                              ? h('div', { style: 'opacity:0.7; font-size:12px; margin-top:8px;' }, 'КП не найдены (ищу папки с artifacts/).')
                              : null
                          ]
                        )
                      ]
                    )
                  ]
                )
              ]
            )
          : null
        ,
        trOpen.value
          ? h(
              'div',
              {
                style:
                  'position:fixed; inset:0; background:rgba(0,0,0,0.35); display:flex; align-items:center; justify-content:center; z-index:9999;'
              },
              [
                h(
                  'div',
                  {
                    style:
                      'width:min(1200px, calc(100vw - 32px)); height:min(760px, calc(100vh - 32px)); background:#fff; border-radius:12px; overflow:hidden; display:flex; flex-direction:column; box-shadow:0 16px 60px rgba(0,0,0,0.35);'
                  },
                  [
                    h(
                      'div',
                      {
                        style:
                          'display:flex; align-items:center; justify-content:space-between; padding:12px 16px; border-bottom:1px solid rgba(0,0,0,0.12);'
                      },
                      [
                        h('strong', {}, 'Выбор расшифровок (ссылки)'),
                        h('button', { onClick: () => (trOpen.value = false), style: 'padding:6px 10px; cursor:pointer;' }, 'Закрыть')
                      ]
                    ),
                    h(
                      'div',
                      { style: 'display:flex; gap:12px; padding:12px 16px; border-bottom:1px solid rgba(0,0,0,0.12);' },
                      [
                        h('span', { style: 'opacity:0.75; font-size:12px; overflow:hidden; text-overflow:ellipsis;' }, `Проект: ${projectFromKp.value}`),
                        h('span', { style: 'opacity:0.75; font-size:12px; overflow:hidden; text-overflow:ellipsis;' }, `КП: ${selectedKpLabel.value}`),
                        h('span', { style: 'opacity:0.75; font-size:12px;' }, trRel.value ? `Папка: /${trRel.value}` : '')
                      ]
                    ),
                    h(
                      'div',
                      { style: 'display:flex; flex:1; min-height:0;' },
                      [
                        h(
                          'div',
                          { style: 'flex:1; padding:12px; overflow:auto; border-right:1px solid rgba(0,0,0,0.12); background:#fafafa;' },
                          [
                            h('div', { style: 'display:flex; gap:8px; align-items:center; margin-bottom:10px;' }, [
                              h('button', { onClick: trUp, style: 'padding:6px 10px; cursor:pointer;' }, '↑'),
                              h(
                                'button',
                                { onClick: loadTrList, style: 'padding:6px 10px; cursor:pointer;' },
                                trLoading.value ? '...' : 'Обновить'
                              ),
                              h('input', {
                                value: trFilter.value,
                                placeholder: 'поиск...',
                                onInput: (e: any) => (trFilter.value = e?.target?.value || ''),
                                style: 'flex:1; padding:6px 10px;'
                              })
                            ]),
                            trErr.value
                              ? h('div', { style: 'color:#b00020; white-space:pre-wrap; font-size:12px; margin-bottom:10px;' }, trErr.value)
                              : null,
                            h('div', { style: 'opacity:0.7; font-size:12px; margin-bottom:8px;' }, 'Папки:'),
                            h(
                              'div',
                              { style: 'display:flex; flex-direction:column; gap:6px; margin-bottom:12px;' },
                              trFilteredDirs.value.map((it: any) =>
                                h(
                                  'button',
                                  {
                                    key: `d:${it.name}`,
                                    onClick: () => trEnter(String(it.name || '')),
                                    style:
                                      'text-align:left; padding:8px 10px; border:1px solid rgba(0,0,0,0.12); border-radius:10px; background:#fff; cursor:pointer;'
                                  },
                                  `📁 ${String(it.name || '')}`
                                )
                              )
                            ),
                            h('div', { style: 'opacity:0.7; font-size:12px; margin-bottom:8px;' }, 'Файлы (любые форматы):'),
                            h(
                              'div',
                              { style: 'display:flex; flex-direction:column; gap:6px;' },
                              trFilteredFiles.value.map((it: any) => {
                                const rel = _trJoin(trRel.value, String(it.name || ''))
                                const checked = !!trSelected.value[rel]
                                return h(
                                  'button',
                                  {
                                    key: `f:${rel}`,
                                    onClick: () => trToggle(rel),
                                    style:
                                      'text-align:left; padding:8px 10px; border:1px solid rgba(0,0,0,0.12); border-radius:10px; background:' +
                                      (checked ? '#e8f0fe' : '#fff') +
                                      '; cursor:pointer;'
                                  },
                                  `${checked ? '✅' : '⬜'} ${String(it.name || '')}`
                                )
                              })
                            )
                          ]
                        ),
                        h(
                          'div',
                          { style: 'width:420px; padding:12px; overflow:auto;' },
                          [
                            h('div', { style: 'display:flex; align-items:center; justify-content:space-between; margin-bottom:10px;' }, [
                              h('strong', {}, `Выбрано: ${trSelectedList.value.length}`),
                              h('button', { onClick: trClear, style: 'padding:6px 10px; cursor:pointer;' }, 'Очистить')
                            ]),
                            h(
                              'div',
                              { style: 'display:flex; flex-direction:column; gap:6px; margin-bottom:12px;' },
                              trSelectedList.value.map((rel) =>
                                h(
                                  'div',
                                  {
                                    key: `s:${rel}`,
                                    style:
                                      'display:flex; gap:8px; align-items:center; padding:8px 10px; border:1px solid rgba(0,0,0,0.12); border-radius:10px; background:#fff;'
                                  },
                                  [
                                    h('div', { style: 'flex:1; font-size:12px; overflow:hidden; text-overflow:ellipsis;' }, rel),
                                    h('button', { onClick: () => trToggle(rel), style: 'padding:6px 10px; cursor:pointer;' }, '×')
                                  ]
                                )
                              )
                            ),
                            h(
                              'button',
                              { onClick: saveTrManifest, disabled: trLoading.value, style: 'padding:10px 12px; cursor:pointer; width:100%;' },
                              trLoading.value ? '...' : 'Сохранить'
                            )
                          ]
                        )
                      ]
                    )
                  ]
                )
              ]
            )
          : null
      ])
  }
})

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'home',
      component: HomeView,
      meta: { requiresAuth: true }
    },
    {
      path: '/chats',
      name: 'chats',
      component: () => import('@/views/ChatsView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/contacts',
      name: 'contacts',
      component: () => import('@/views/ContactsView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/industries',
      name: 'industries',
      component: () => import('@/views/IndustriesView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/mn-client-invoices',
      name: 'mnClientInvoices',
      component: () => import('@/views/MnClientInvoicesView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/crm-aggregation-rules',
      name: 'crmAggregationRules',
      component: () => import('@/views/CrmAggregationRulesView.vue'),
      meta: { requiresAuth: true, requiresAdmin: true }
    },
    {
      path: '/company-domain-reviews',
      name: 'companyDomainReviews',
      component: () => import('@/views/CompanyDomainReviewsView.vue'),
      meta: { requiresAuth: true, requiresAdmin: true }
    },
    {
      path: '/crm-outreach',
      name: 'crmOutreach',
      component: () => import('@/views/CrmOutreachView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/tasks',
      name: 'tasks',
      component: () => import('@/views/TasksView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/task-board',
      name: 'taskBoard',
      component: () => import('@/views/TaskBoardView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/analytics',
      name: 'analytics',
      component: () => import('@/views/AnalyticsView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/daily-reports',
      name: 'dailyReports',
      component: () => import('@/views/DailyReportsView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/analytics-dialogs',
      name: 'analyticsDialogs',
      component: () => import('@/views/AnalyticsDialogsView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/analytics-dashboard',
      name: 'analyticsDashboard',
      component: () => import('@/views/AnalyticsDashboardView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/analytics-auto-jobs',
      name: 'analyticsAutoJobs',
      component: () => import('@/views/AnalyticsAutoJobsView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/llm-logs',
      name: 'llmLogs',
      component: () => import('@/views/LlmLogsView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/llm-evals',
      name: 'llmEvals',
      component: () => import('@/views/LlmEvalsView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/llm-evals-tv',
      name: 'llmEvalsTv',
      component: () => import('@/views/LlmEvalsTvView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/custdev-tv',
      name: 'custDevTv',
      component: () => import('@/views/CustDevTvDashboardView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/drafts',
      name: 'drafts',
      redirect: { name: 'tasks', query: { view: 'drafts' } },
      meta: { requiresAuth: true }
    },
    {
      path: '/finance-wiki',
      name: 'financeWiki',
      component: () => import('@/views/FinanceWikiView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/wiki',
      name: 'wiki',
      component: () => import('@/views/WikiView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/wiki/:id',
      name: 'wikiPage',
      component: () => import('@/views/WikiPageView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/messenger',
      name: 'messenger',
      component: () => import('@/views/MessengerView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/messenger/user/:telegramId',
      name: 'telegramUserCard',
      component: () => import('@/views/TelegramUserCardView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/telethon',
      name: 'telethon',
      component: () => import('@/views/TelethonAccountsView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/telethon-settings/:accountId',
      name: 'telethonSettings',
      component: () => import('@/views/TelethonSettingsView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/ai-bots',
      name: 'aiBots',
      component: () => import('@/views/AIBotsView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/ai-staff',
      name: 'aiStaff',
      component: () => import('@/views/AiStaffView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/email-accounts',
      name: 'emailAccounts',
      component: () => import('@/views/EmailAccountsView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/login',
      name: 'login',
      component: LoginView
    },
    {
      path: '/register',
      name: 'register',
      component: RegisterView
    },
    {
      path: '/notes',
      name: 'notes',
      component: () => import('@/views/NotesView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/tags',
      name: 'tags',
      component: () => import('@/views/TagsView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/users',
      name: 'users',
      component: () => import('@/views/UsersView.vue'),
      meta: { requiresAuth: true, requiresAdmin: true }
    },
    {
      path: '/telegram',
      name: 'telegram',
      component: () => import('@/views/TelegramView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/llm',
      name: 'llm',
      component: () => import('@/views/LLMView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/settings',
      name: 'settings',
      component: () => import('@/views/SettingsView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/manager-settings',
      name: 'managerSettings',
      component: () => import('@/views/ManagerSettingsView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/system-settings',
      name: 'systemSettings',
      component: () => import('@/views/SystemSettingsView.vue'),
      meta: { requiresAuth: true, requiresAdmin: true }
    },
    {
      path: '/tenants',
      name: 'tenants',
      component: () => import('@/views/TenantsManagementView.vue'),
      meta: { requiresAuth: true, requiresAdmin: true }
    },
    {
      path: '/invitations',
      name: 'invitations',
      component: () => import('@/views/InvitationsView.vue'),
      meta: { requiresAuth: true, requiresAdmin: true }
    },
    {
      path: '/create-tenant',
      name: 'createTenant',
      component: () => import('@/views/CreateTenantView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/proxy-management',
      name: 'proxyManagement',
      component: () => import('@/views/ProxyManagementView.vue'),
      meta: { requiresAuth: true, requiresAdmin: true }
    },
    {
      path: '/proxy-assignment',
      name: 'proxyAssignment',
      component: () => import('@/views/ProxyAssignmentView.vue'),
      meta: { requiresAuth: true, requiresAdmin: true }
    },
    {
      path: '/telethon-events',
      name: 'telethonEvents',
      component: () => import('@/views/TelethonEventsAdminView.vue'),
      meta: { requiresAuth: true, requiresAdmin: true }
    },
    {
      path: '/oauth/callback',
      name: 'oauthCallback',
      component: () => import('@/views/OAuthCallbackView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/kp',
      name: 'kp',
      component: KPView,
      meta: { requiresAuth: true }
    },
    {
      path: '/shareholders-report',
      name: 'shareholdersReport',
      component: () => import('@/views/ShareholdersReportView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/commercial-pnl',
      name: 'commercialPnl',
      component: () => import('@/views/CommercialPnlView.vue'),
      meta: { requiresAuth: true }
    }
  ]
})

// Accept JWT via ?auth_token= query param (for automation / Cursor browser)
router.beforeEach((to, from, next) => {
  const authToken = to.query.auth_token as string | undefined
  if (authToken) {
    localStorage.setItem('token', authToken)
    const query = { ...to.query }
    delete query.auth_token
    next({ path: to.path, query })
    return
  }

  const token = localStorage.getItem('token')
  
  if (to.meta.requiresAuth && !token) {
    next('/login')
    return
  }

  if (to.meta.requiresAdmin) {
    try {
      if (!token) {
        next('/login')
        return
      }
      const payload = JSON.parse(atob(token.split('.')[1]))
      const role = payload?.role
      if (!['tenant_admin', 'platform_admin'].includes(role)) {
        next('/')
        return
      }
    } catch (e) {
      next('/')
      return
    }
  }

  next()
})

export default router
